import type { Project, StageDefinition } from './types';
import { currentOutputReview, normalizeCampaign, scopeApproved } from '../features/studio/marketing/model';

function stage(id: string, number: string, label: string, plain: string, title: string, sub: string, agent: string, skill: string): StageDefinition {
  return { id, number, label, plain, title, sub, agent, skill };
}

export const STAGES: StageDefinition[] = [
  stage('idea', '01', 'Story & Script', 'Story & Script', 'Shape the story and write the screenplay', 'Cue develops the story, writes the screenplay and extracts production anchors using one shared project record.', 'Cue', 'story-architect'),
  stage('world', '02', 'World', 'Create the cast & places', 'Create the cast & places', 'Open a card from the locked screenplay, generate its still, and lock it so every shot stays consistent.', 'Cue', 'world-builder'),
  stage('plan', '03', 'Direct', 'Direct each scene', 'Scene Direct', 'Direct shots, sketch compositions and generate scene or shot video from a locked plan.', 'Cue', 'shot-planner'),
  stage('generate', '04', 'Production', 'Review production and dailies', 'Production & dailies', 'Track scene readiness, generation attempts and approved selects. Open a scene to direct or generate its video.', 'Cue', 'model-router'),
  stage('edit', '05', 'Edit', 'Assemble the film', 'Choose takes and shape the cut', 'Pull selected footage into the timeline, then adjust timing and export or render. Sound design will live in the editor.', 'Cue', 'edit-advisor'),
  stage('review', '06', 'Internal Finish Review', 'Review & deliver', 'Internal finish review, approval and delivery', 'Resolve internal finish checks, then share a separate External Director Review when needed.', 'Cue Review', 'delivery-supervisor')
];

export const VFX_STAGES: StageDefinition[] = [
  stage('edit', '01', 'Workspace', 'Workspace', 'Bring the plate', 'Shape a new world with your VFX agent, tracked mattes and Seedance edits.', 'VFX Supervisor', 'vfx-supervision'),
  stage('review', '02', 'Review', 'Review', 'Review the picture', 'Compare versions against the plate and lock the select before delivery.', 'VFX Supervisor', 'vfx-supervision'),
  stage('deliver', '03', 'Deliver', 'Deliver', 'From plate to pipeline', 'Export MP4, ProRes or an EXR sequence with a delivery manifest.', 'VFX Supervisor', 'delivery-supervisor')
];

export const MARKETING_STAGES: StageDefinition[] = [
  stage('onboard', '01', 'Scope', 'Define deliverables', 'Agree what to make', 'Cue coordinates specialist skills around the campaign brief and requested order.', 'Cue', 'marketing-onboard'),
  stage('produce', '02', 'Create', 'Create outputs', 'Make the ordered work', 'Cue coordinates writing and production for the approved scope.', 'Cue', 'marketing-production'),
  stage('approve', '03', 'Review', 'Review outputs', 'Inspect the current revisions', 'A human reviewer checks each output against its acceptance criteria.', 'Cue', 'marketing-delivery-qc'),
  stage('deliver', '04', 'Delivery', 'Ready to package', 'Package approved work', 'Create the delivery ZIP from the reviewed, ready outputs.', 'Cue', 'marketing-delivery-qc')
];

export const SAAS_STAGES: StageDefinition[] = [
  stage('brief', '01', 'Brief', 'Brief', 'What should your product say?', 'Lock the walkthrough in one sentence before Kinetic starts rebuilding screens.', 'Kinetic', 'saas-director'),
  stage('brand', '02', 'Brand', 'Brand', 'Make every frame feel like you', 'Set type, colour and tone so every scene stays on-brand.', 'Kinetic', 'saas-director'),
  stage('screens', '03', 'Sources', 'Sources', 'Bring the real product', 'Capture or upload the screens the film will actually show.', 'Kinetic', 'saas-director'),
  stage('rebuild', '04', 'Rebuild', 'Rebuild', 'Every detail, on its own layer', 'Reconstruct UI so motion can hit real controls, not a flat screenshot.', 'Kinetic', 'saas-director'),
  stage('storyboard', '05', 'Storyboard', 'Storyboard', 'Give the story a shape', 'Beat out the walkthrough before anything is animated.', 'Kinetic', 'saas-director'),
  stage('motion', '06', 'Motion', 'Motion', 'Make the details move', 'Direct camera, cursor and layer animation on the rebuilt screens.', 'Kinetic', 'saas-director'),
  stage('audio', '07', 'Audio', 'Audio', 'Give the story a voice', 'Add VO and mix against the locked picture.', 'Kinetic', 'saas-director'),
  stage('review', '08', 'Review', 'Review & export', 'Check the cut. Deliver the film', 'QC the walkthrough, then export.', 'Kinetic', 'saas-director')
];

export const UPSCALE_STAGES: StageDefinition[] = [
  stage('upscale', '01', 'Upscale', 'Upscale', 'Upscale clips', 'Upload footage, pick resolution, confirm spend.', 'Post', 'upscale')
];

export const HOME_PIPELINES: Array<{ id: string; number: string; title: string; stages: StageDefinition[] }> = [
  { id: 'film', number: '01', title: 'Film', stages: STAGES },
  { id: 'vfx', number: '02', title: 'RAW 2.0', stages: VFX_STAGES },
  { id: 'marketing', number: '03', title: 'Campaign', stages: MARKETING_STAGES },
  { id: 'saas', number: '04', title: 'SaaS Studio', stages: SAAS_STAGES },
  { id: 'upscale', number: '05', title: 'Upscale', stages: UPSCALE_STAGES }
];

export const VFX_TYPES = [
  'Film VFX',
  'Commercial VFX',
  'Cleanup and replacement',
  'Environment extension',
  'Creature and character work'
];

export const MARKETING_TYPES = ['Product ad', 'Social campaign', 'Performance creative', 'Brand film ad'];

export const isVfxProject = (p?: Project | null): boolean =>
  Boolean(p && (p.projectKind === 'ai-vfx' || VFX_TYPES.includes(p.type)));

export const isMarketingProject = (p?: Project | null): boolean =>
  Boolean(p && p.system !== 'global-generator' && (p.projectKind === 'marketing' || p.marketingWorkspace || MARKETING_TYPES.includes(p.type)));

export const isUpscaleProject = (p?: Project | null): boolean =>
  Boolean(p && (p.projectKind === 'upscale' || p.upscaleWorkspace || p.type === 'Upscale'));

export function projectPipelineId(p?: Project | null): string {
  if (p?.projectKind === 'saas') return 'saas';
  if (isVfxProject(p)) return 'vfx';
  if (isUpscaleProject(p)) return 'upscale';
  if (isMarketingProject(p)) return 'marketing';
  return 'film';
}

export function projectPipelineStages(p?: Project | null): StageDefinition[] {
  const id = projectPipelineId(p);
  return (HOME_PIPELINES.find(item => item.id === id) || HOME_PIPELINES[0]).stages;
}

function currentStageId(p: Project): string {
  if (p.projectKind === 'saas') {
    const workspace = p.saasWorkspace as { step?: string } | undefined;
    return workspace?.step || p.stage || 'brief';
  }
  if (isVfxProject(p) && (p.stage === 'generate' || !p.stage)) return 'edit';
  if (isMarketingProject(p)) {
    const campaign = normalizeCampaign(p.marketingWorkspace?.campaign);
    if (!scopeApproved(campaign)) return 'onboard';
    const outputs = campaign.deliverables.map(deliverable => campaign.content.find(item => item.deliverableId === deliverable.id));
    if (outputs.some(item => !item || item.status === 'draft')) return 'produce';
    if (outputs.some(item => item?.status !== 'ready' || !currentOutputReview(item, campaign))) return 'approve';
    return 'deliver';
  }
  if (projectPipelineId(p) !== 'film') return p.stage;
  const scenes = Array.isArray(p.scenes) ? p.scenes as Record<string, unknown>[] : [];
  const timeline = p.timeline as { clips?: unknown[]; tracks?: { clips?: unknown[] }[] } | undefined;
  let evidenced = p.scriptApproved ? 1 : 0;
  if ((p.shots || []).length || scenes.some(scene => Boolean((scene.direction as { text?: string; locked?: boolean } | undefined)?.text || (scene.direction as { locked?: boolean } | undefined)?.locked))) evidenced = Math.max(evidenced, 2);
  if (scenes.some(scene => Boolean(scene.selectedVideoId)) || (p.shots || []).some(shot => Array.isArray(shot.takes) && shot.takes.some(take => take.type === 'video'))) evidenced = Math.max(evidenced, 3);
  if ((timeline?.clips || []).length || (timeline?.tracks || []).some(track => Boolean(track.clips?.length))) evidenced = Math.max(evidenced, 4);
  const stored = STAGES.findIndex(stage => stage.id === p.stage);
  return STAGES[Math.max(evidenced, stored, 0)].id;
}

export function projectStageMeta(p: Project) {
  const stages = projectPipelineStages(p);
  const index = stages.findIndex(item => item.id === currentStageId(p));
  const current = index >= 0 ? index : 0;
  let info = stages[current] || stages[0];
  if (isMarketingProject(p) && info.id === 'deliver') {
    const campaign = normalizeCampaign(p.marketingWorkspace?.campaign);
    const packaged = campaign.receipts.some(receipt => receipt.scopeRevision === campaign.scope.revision && receipt.outputs.length === campaign.deliverables.length && receipt.outputs.every(output => campaign.content.some(item => item.id === output.id && item.deliverableId === output.deliverableId && item.revision === output.revision)));
    info = { ...info, plain: packaged ? 'Packaged' : 'Ready to package' };
  }
  return { stages, current, info };
}

export function projectKindLabel(p: Project): string {
  if (p.projectKind === 'saas') return 'SaaS Studio';
  if (isUpscaleProject(p)) return 'upscale';
  if (isMarketingProject(p)) return 'marketing';
  if (isVfxProject(p)) return 'vfx';
  return 'cinema';
}
